import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const CreativeVariant = ({ heroContent, brand }) => {
  const { cta, domainConfig } = useTheme();
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100">
      {/* Hero Section - Creative Artistic Design */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Creative Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full opacity-20 transform rotate-45"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full opacity-30 transform -rotate-12"></div>
          <div className="absolute bottom-20 left-40 w-40 h-40 bg-gradient-to-r from-pink-400 to-orange-400 rounded-full opacity-25 transform rotate-45"></div>
          <div className="absolute top-60 left-1/2 w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full opacity-30 transform rotate-12"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Creative Brand Badge */}
          <div className="mb-8">
            <span className="inline-block px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-pink-500 rounded-full transform -rotate-2 shadow-lg">
              ✨ {brand.name}
            </span>
          </div>

          {/* Main Content Grid - Asymmetric */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <h1 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6 transform -rotate-1">
                  <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                    {h1Title || title}
                  </span>
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed font-medium transform rotate-1">
                  {subtitle}
                </p>
              </div>

              {/* Features - Creative Cards */}
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className={`p-4 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300 ${
                    index % 2 === 0 
                      ? 'bg-gradient-to-r from-orange-400 to-pink-400 text-white -rotate-1' 
                      : 'bg-gradient-to-r from-purple-400 to-blue-400 text-white rotate-1'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {index % 4 === 0 ? '🎨' : index % 4 === 1 ? '✨' : index % 4 === 2 ? '🚀' : '💫'}
                      </div>
                      <span className="font-bold">{feature}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons - Creative Style */}
              <div className="flex space-x-4 pt-8">
                {renderCtaButton(cta?.primary, 'Get Started', 'px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-full hover:from-orange-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105', domainConfig?.shopLink)}
                {renderCtaButton(cta?.secondary, 'Explore', 'px-8 py-4 border-4 border-purple-400 text-purple-600 font-bold rounded-full hover:bg-purple-400 hover:text-white transition-all duration-300 transform hover:rotate-2', domainConfig?.shopLink)}
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="relative">
              {/* Main Image with Creative Frame */}
              <div className="relative transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 to-pink-400 rounded-3xl transform rotate-6"></div>
                <div className="relative">
                  <img 
                    ref={mainImageRef}
                    src={mainImage} 
                    alt={title}
                    className="w-full h-96 object-cover rounded-2xl shadow-2xl"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                  />
                </div>
              </div>
              
              {/* Secondary Image - Creative Overlay */}
              <div className="absolute -bottom-8 -left-8 w-48 h-32 transform -rotate-12 hover:rotate-0 transition-transform duration-500">
                <div className="absolute -inset-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-2xl transform rotate-6"></div>
                <div className="relative">
                  <img 
                    ref={secondaryImageRef}
                    src={secondaryImage} 
                    alt={`${title} - Secondary`}
                    className="w-full h-full object-cover rounded-xl shadow-xl"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                  />
                </div>
              </div>

              {/* Creative Elements */}
              <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center transform rotate-12 shadow-lg">
                <div className="text-2xl">🎨</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Creative Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '500+', label: 'Creative Projects', color: 'from-orange-400 to-pink-400', icon: '🎨' },
              { number: '99.9%', label: 'Innovation Rate', color: 'from-purple-400 to-blue-400', icon: '✨' },
              { number: '24/7', label: 'Creative Support', color: 'from-pink-400 to-purple-400', icon: '🚀' },
              { number: '5★', label: 'Art Rating', color: 'from-yellow-400 to-orange-400', icon: '💫' }
            ].map((stat, index) => (
              <div key={index} className={`text-center p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300 ${
                index % 2 === 0 ? 'rotate-2' : '-rotate-2'
              }`}>
                <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-full mx-auto mb-4 flex items-center justify-center text-2xl`}>
                  {stat.icon}
                </div>
                <div className={`text-3xl font-black mb-2 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.number}
                </div>
                <div className="text-gray-700 font-bold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Creative Showcase */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4 transform -rotate-1">
              <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                Creative Excellence
              </span>
            </h2>
            <p className="text-xl text-gray-700 font-medium transform rotate-1">
              Where imagination meets innovation in every project
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className={`group p-8 rounded-3xl shadow-lg transform hover:scale-105 transition-all duration-300 ${
                index % 3 === 0 
                  ? 'bg-gradient-to-br from-orange-400 to-pink-400 text-white rotate-2' 
                  : index % 3 === 1 
                  ? 'bg-gradient-to-br from-purple-400 to-blue-400 text-white -rotate-2' 
                  : 'bg-gradient-to-br from-pink-400 to-purple-400 text-white rotate-1'
              }`}>
                <div className="text-4xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {index % 4 === 0 ? '🎨' : index % 4 === 1 ? '✨' : index % 4 === 2 ? '🚀' : '💫'}
                </div>
                <h3 className="text-2xl font-black mb-4">{feature}</h3>
                <p className="leading-relaxed font-medium">
                  Creative solutions that push boundaries and inspire innovation in every aspect of your business.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Creative Footer */}
      <section className="py-16 bg-gradient-to-r from-orange-400 to-pink-400">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-black text-white mb-4 transform -rotate-1">
            Ready to Create Something Amazing?
          </h3>
          <p className="text-xl text-white/90 mb-8 font-medium transform rotate-1">
            Let's turn your vision into reality with our creative solutions
          </p>
          {renderCtaButton(cta?.primary, 'Start Creating', 'px-12 py-4 bg-white text-orange-500 font-black rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105', domainConfig?.shopLink)}
        </div>
      </section>
    </div>
  );
};

export default CreativeVariant;
